import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, MapPin, Building2 } from "lucide-react";
import useSeo from "@/hooks/useSeo";
import useJsonLd from "@/hooks/useJsonLd";
import ContourBackground from "@/components/ContourBackground";
import Reveal from "@/components/Reveal";
import { getStateBySlug } from "@/data/usLocations";
import { SERVICES } from "@/data/services";
import { CAD_SERVICES } from "@/data/cadServices";
import { SURVEY_KEYWORDS, CAD_KEYWORDS, composeKeywords } from "@/data/seoKeywords";
import { stateLd, breadcrumb } from "@/lib/jsonLd";

export default function StatePage() {
  const { stateSlug } = useParams();
  const state = getStateBySlug(stateSlug);

  const services = [...SERVICES, ...CAD_SERVICES];

  useSeo({
    title: state
      ? `CAD Drafting Services in ${state.state} (${state.abbr}) — Tri-Axis XYZ`
      : "Coverage — Tri-Axis XYZ",
    description: state
      ? `Outsource land-survey CAD, ALTA, boundary, topo, plats, MEP/BIM, structural and civil drafting in ${state.state}. Serving ${state.counties.length}+ counties incl. ${state.counties.slice(0, 3).map((c) => c.seat).join(", ")}.`
      : "",
  });

  useJsonLd(state ? stateLd(state, services) : null, `state-${stateSlug}`);
  useJsonLd(
    state
      ? breadcrumb([
          { name: "Home", path: "/" },
          { name: "Coverage", path: "/cad-services" },
          { name: state.state, path: `/cad-services/${state.slug}` },
        ])
      : null,
    `state-bc-${stateSlug}`,
  );

  if (!state) return <Navigate to="/cad-services" replace />;

  return (
    <div data-testid="page-state">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200 bg-paper-dark/40">
        <ContourBackground variant="light" opacity={0.5} seed={state.slug.length} />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 py-14 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <nav className="mono text-[10px] tracking-[0.22em] uppercase text-topo flex items-center gap-2">
              <Link to="/cad-services" className="hover:text-emerald-700">
                Coverage
              </Link>
              <span>›</span>
              <span>{state.region}</span>
              <span>›</span>
              <span className="text-slate-900">{state.state}</span>
            </nav>
            <h1
              data-testid="state-page-title"
              className="display mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-slate-900 leading-[1.02]"
            >
              CAD drafting & land-survey CAD —{" "}
              <span className="text-emerald-700">{state.state}</span>
            </h1>
            <p className="mt-5 max-w-3xl text-base sm:text-lg text-slate-600 leading-relaxed">
              Tri-Axis XYZ runs dedicated CAD production for surveying,
              architectural, engineering and construction firms across{" "}
              <strong>{state.state}</strong>. From{" "}
              <strong>{state.counties[0]?.seat}</strong> to{" "}
              <strong>{state.counties[state.counties.length - 1]?.seat}</strong>,
              we deliver Civil 3D, AutoCAD, MicroStation, Revit MEP and ArcGIS
              outputs licensed surveyors stamp without hesitation.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to={`/contact?country=USA&state=${encodeURIComponent(state.state)}&code=${state.abbr}`}
                data-testid="state-cta-quote"
                className="inline-flex items-center gap-2 rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-semibold px-5 py-3"
              >
                Request {state.abbr} quote <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center gap-2 rounded-sm border border-slate-300 hover:border-slate-900 text-slate-900 text-sm font-semibold px-5 py-3 bg-white"
              >
                Browse all services
              </Link>
            </div>
          </div>
          <aside className="lg:col-span-4">
            <div className="border border-slate-300 bg-white p-5">
              <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">
                {state.state} — quick facts
              </div>
              <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <dt className="text-slate-500">Region</dt>
                  <dd className="font-bold text-slate-900">{state.region}</dd>
                </div>
                <div>
                  <dt className="text-slate-500">State code</dt>
                  <dd className="font-bold text-slate-900">{state.abbr}</dd>
                </div>
                <div>
                  <dt className="text-slate-500">Counties served</dt>
                  <dd className="font-bold text-slate-900">{state.counties.length}+</dd>
                </div>
                <div>
                  <dt className="text-slate-500">ZIPs covered</dt>
                  <dd className="font-bold text-slate-900">
                    {state.counties.reduce((a, c) => a + c.zips.length, 0)}+
                  </dd>
                </div>
              </dl>
            </div>
          </aside>
        </div>
      </section>

      {/* County grid */}
      <section className="bg-white border-b border-slate-200 py-14">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">
              Counties in {state.state}
            </div>
            <h2 className="display mt-2 text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900">
              {state.counties.length} active counties · click for ZIP coverage
            </h2>
          </Reveal>
          <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {state.counties.map((c) => (
              <li key={c.slug}>
                <Link
                  to={`/cad-services/${state.slug}/${c.slug}`}
                  data-testid={`county-link-${c.slug}`}
                  className="group block border border-slate-200 bg-white hover:border-emerald-600 hover:shadow-sm transition p-4"
                >
                  <div className="flex items-center gap-2 text-emerald-700">
                    <Building2 className="h-4 w-4" />
                    <span className="mono text-[10px] tracking-[0.18em] uppercase">
                      {state.abbr} County
                    </span>
                  </div>
                  <div className="mt-1 font-extrabold text-slate-900">{c.name}</div>
                  <div className="mt-1 text-xs text-slate-600">
                    <MapPin className="inline h-3 w-3 mr-1 -mt-0.5 text-slate-500" />
                    Seat: <strong>{c.seat}</strong>
                  </div>
                  <div className="mt-2 mono text-[10px] tracking-wide text-slate-500 line-clamp-2">
                    {c.zips.slice(0, 5).join(" · ")}
                    {c.zips.length > 5 ? ` · +${c.zips.length - 5} more` : ""}
                  </div>
                  <div className="mt-3 inline-flex items-center text-xs font-semibold text-emerald-700">
                    See county detail
                    <ArrowRight className="ml-1 h-3 w-3 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all" />
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Services × State keywords */}
      <section className="bg-paper-dark/30 border-b border-slate-200 py-14">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-10">
          <Reveal>
            <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">
              🧭 Land-Survey CAD in {state.state}
            </div>
            <h3 className="display mt-2 text-2xl font-extrabold tracking-tight text-slate-900">
              11 survey-CAD disciplines, statewide
            </h3>
            <ul className="mt-4 grid grid-cols-2 gap-2 text-sm">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/services/${s.slug}`}
                    className="text-slate-700 hover:text-emerald-700"
                  >
                    {s.emoji} {s.name}
                  </Link>
                </li>
              ))}
            </ul>
            <p className="mt-4 text-xs text-slate-500 leading-relaxed">
              {composeKeywords(SURVEY_KEYWORDS).slice(0, 600)}…
            </p>
          </Reveal>
          <Reveal>
            <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">
              📐 CAD Drafting Beyond Survey · {state.state}
            </div>
            <h3 className="display mt-2 text-2xl font-extrabold tracking-tight text-slate-900">
              12 CAD disciplines for {state.state} AEC firms
            </h3>
            <ul className="mt-4 grid grid-cols-2 gap-2 text-sm">
              {CAD_SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/cad-techies#cad-${s.slug}`}
                    className="text-slate-700 hover:text-emerald-700"
                  >
                    {s.emoji} {s.name}
                  </Link>
                </li>
              ))}
            </ul>
            <p className="mt-4 text-xs text-slate-500 leading-relaxed">
              {composeKeywords(CAD_KEYWORDS).slice(0, 600)}…
            </p>
          </Reveal>
        </div>
      </section>

      {/* CTA strip */}
      <section className="bg-cyanotype text-white py-14">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="mono text-[10px] tracking-[0.22em] uppercase text-emerald-300">
              Ready to brief us?
            </div>
            <h3 className="display mt-2 text-2xl md:text-3xl font-extrabold tracking-tight">
              Get a {state.state} CAD-drafting quote in 1 business day.
            </h3>
          </div>
          <Link
            to={`/contact?country=USA&state=${encodeURIComponent(state.state)}&code=${state.abbr}`}
            data-testid="state-cta-quote-bottom"
            className="inline-flex items-center gap-2 rounded-sm bg-emerald-500 hover:bg-emerald-400 text-slate-900 text-sm font-bold px-5 py-3"
          >
            Request {state.abbr} quote <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
